import { useState } from 'react'

import { NpsWidgetVisibilityDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'

import { useCreateNpsIntent } from 'modules/nps/hooks/useCreateNpsIntent'
import { useNpsForm } from 'modules/nps/hooks/useNpsForm'
import { useNpsVisibilityForPath } from 'modules/nps/hooks/useNpsVisibility'

import {
  _FeedbackStep as FeedbackStep,
  _ScoringStep as ScoringStep,
  _ThankYouStep as ThankYouStep,
} from './_Steps'
import { ValidNpsStep } from './types'

type Props = {
  currentPath: string
}

const Widget = ({ currentPath }: Props) => {
  const INITIAL_STEP: ValidNpsStep = 'score'
  const [step, setStep] = useState<ValidNpsStep>(INITIAL_STEP)
  const { create: createIntent } = useCreateNpsIntent()
  const { icon, productId, productName, shouldShow, variant } = useNpsVisibilityForPath(currentPath)
  const [isOpen, setIsOpen] = useState(shouldShow)
  const { data, loading } = useQuery(NpsWidgetVisibilityDocument)
  const canSeeWidget = data?.nps._policies.showNpsWidget.can

  const onClose = () => {
    setIsOpen(false)
  }

  const onCloseAndTrack = () => {
    track(trackingEvents.nps.closesWidget, {
      step,
      product_id: productId,
      path: currentPath,
    })
    onClose()
  }

  const onSuccess = () => {
    track(trackingEvents.nps.submitNpsAnswer, { path: currentPath })
  }

  const { isFormValid, isSubmitting, onChange, onSubmit, resetForm, values } = useNpsForm(
    productId,
    onSuccess
  )

  useEffect(() => {
    if (loading || !canSeeWidget) return

    setIsOpen(shouldShow)

    if (shouldShow)
      track(trackingEvents.nps.viewsWidget, {
        product_id: productId,
        path: currentPath,
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeWidget, loading, shouldShow])

  useEffect(() => {
    setStep(INITIAL_STEP)
    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath])

  if (!isOpen || !canSeeWidget) return null

  return (
    <>
      {step === 'score' && (
        <ScoringStep
          variant={variant}
          icon={icon}
          moduleName={productName}
          onCompleteStep={async () => {
            if (isFormValid) {
              const result = await createIntent({ origin: productId })
              const handled = handleResult(result, () => undefined)
              if (handled.success) {
                onChange('surveyIntentId', handled.data.surveyResponseIntent?.id)
              }

              track(trackingEvents.nps.completesScoringStep, {
                product_id: productId,
                path: currentPath,
              })
              setStep('feedback')
            }
          }}
          onChangeValue={(value) => {
            onChange('scoring', value)
          }}
          onClose={() => {
            createIntent({ origin: productId })
            onCloseAndTrack()
          }}
        />
      )}
      {step === 'feedback' && (
        <FeedbackStep
          variant={variant}
          icon={icon}
          onCompleteStep={async () => {
            if (isFormValid) {
              await onSubmit()
              setStep('thank-you')
            }
          }}
          onChangeValue={(written) => {
            onChange('feedback', written)
          }}
          onClose={async () => {
            await onSubmit()
            onCloseAndTrack()
          }}
          isDetractor={Number(values.scoring) < 6}
          isSubmitting={isSubmitting}
          currentValue={values.feedback}
        />
      )}
      {step === 'thank-you' && <ThankYouStep onClose={onClose} scoring={values.scoring} />}
    </>
  )
}

export default Widget
